<template>
  <div>
    <RedirectModal
      :show="showRedirectModal"
      @close="showRedirectModal = false"
    />
    <v-skeleton-loader
      v-if="loading"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <VTableLocal
      v-else-if="hasPermission('treatments.view')"
      :iconHeader="hasPermission('sites.view') && hasPermission('treatments.add') ? 'add' : null"
      :items="displayedTreatments"
      isSearchable
      :headers="tableHeaders"
      :itemActions="actions"
      :title="$t('treatmentPrograms.pageName')"
      @iconAction="onClickAction"
      @click-icon-header="onClickAdd"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import { cloneDeep } from 'lodash';
import VTableLocal from '@/components/shared/content-views/VTableLocal';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import Main from '@/components/forms/treatmentProgram/main';
import steamForm from '@/components/forms/treatmentProgram/steamForm';
import Http from '@/constants/httpCode';
import RedirectModal from '@/components/shared/RedirectModal.vue';
// eslint-disable-next-line import/no-cycle
import lockService from '@/utilities/lockService';

export default {
    components: {
        VTableLocal,
        RedirectModal,
    },
    data() {
        return {
            showRedirectModal: false,
            treatments: [],
            loading: false,
            componentId: null,
            actionName: 'treatmentProgram',
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'network',
            'hasPermission',
            'component',
            'treatmentProgramUnits',
        ]),
        tableHeaders() {
            if (this.isNetworkSteam) {
                return [
                    'productName',
                    'function',
                    'targetDosage',
                    'injectionPoint',
                    'injectionControl',
                ].map(header => ({
                    text: this.$t(`treatmentProgram.steam.headers.${header}`),
                    value: header,
                }));
            }
            return [
                'productName',
                'function',
                'targetDosage',
                'injectionMethod',
                'volumeSystem',
            ].map(header => ({
                text: this.$t(`treatmentProgram.headers.${header}`),
                value: header,
            }));
        },
        actions() {
            const actions = [];

            if (this.hasPermission('treatments.edit')) {
                actions.push({
                    name: this.$t('forms.btn.edit'),
                    icon: 'edit',
                    mode: 'edit',
                });
            }

            if (this.hasPermission('treatments.remove')) {
                actions.push({
                    name: this.$t('forms.btn.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }

            return actions;
        },
        displayedTreatments() {
            if (this.isNetworkSteam) return this.displaySteamTreatment();
            return this.treatments.map(treatment => ({
                id: treatment.id,
                // Todo treatment.programInfo.name no longer will be valid for new treatments, but for the old ones
                // till all treatments get updated we show the name(text), and should remove after
                productName: treatment.programInfo?.siteProduct?.product?.name || treatment.programInfo?.name,
                function: this.getFunction(treatment.function),
                injectionMethod: treatment.injectionMethod?.controleInjection !== null
                    ? `${this.getUnits('controleInjection', treatment.injectionMethod?.controleInjection)}` : '-',
                targetDosage: treatment.targetDosage?.dosage?.value !== null
                    ? `${treatment.targetDosage?.dosage?.value} ${this.getUnits('dosageUnit', treatment.targetDosage?.dosage?.unit)}`
                    : '-',
                injectionTime: treatment.targetDosage?.injectionCible?.value !== null
                    ? `${treatment.targetDosage?.injectionCible?.value} ${this.getUnits('injectionTimeUnit', treatment.targetDosage?.injectionTime?.unit)}`
                    : '-',
                volumeSystem: treatment.targetDosage?.volumeSystem?.value !== null
                    ? `${treatment.targetDosage?.volumeSystem?.value} ${this.getUnits('volumeSystemUnit', treatment.targetDosage?.volumeSystem?.unit)}`
                    : '-',
            }));
        },
        isNetworkSteam() {
            return this.network.type.key === 'steam';
        },
    },
    watch: {
        userLang: {
            async handler() {
                if (this.treatmentProgramUnits) {
                    await this.loadUnits({ lang: this.userLang });
                    this.displayedTreatments();
                }
            },
            deep: true,
        },
    },
    async mounted() {
        window.addEventListener('beforeunload', this.handleUnload);
    },
    async beforeRouteLeave(to, from, next) {
        try {
            if (this.component && this.component.id) {
                const releaseStatus = await lockService.releaseLock(this.component.id, this.actionName);
                if (!releaseStatus) return next(false);
            }
        } catch (error) {
            console.error('Error releasing lock on route leave:', error);
            // Continue navigation even if lock release fails
        }
        return next();
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this.handleUnload);
        // Ensure locks are released even if beforeRouteLeave didn't complete
        if (this.component && this.component.id) {
            lockService.releaseLock(this.component.id, this.actionName)
                .catch(err => console.error('Error releasing lock on destroy:', err));
        }
    },
    async created() {
        if (!this.treatmentProgramUnits) {
            await this.loadUnits({ lang: this.userLang });
        }
        await this.getTreatmentPrograms();
        if (!this.$store.getters.lockFrom) {
            await this.checkLockOnLoad();
        }
    },
    methods: {
        ...mapActions({
            loadUnits: 'getTreatmentUnits',
        }),
        handleUnload() {
            if (this.component && this.component.id) {
                // Handle synchronously for page unload events
                try {
                    lockService.releaseLock(this.component.id, this.actionName);
                } catch (error) {
                    console.error('Error releasing lock on unload:', error);
                }
            }
        },
        async checkLockOnLoad() {
            try {
                const lockAvailable = await lockService.getLockStatus(this.component.id, this.actionName);
                if (!lockAvailable) {
                    this.showRedirectModal = true;
                    return;
                }
                await lockService.requestLock(this.component.id, this.actionName);
            } catch (error) {
                this.showRedirectModal = true;
            }
        },
        async getTreatmentPrograms() {
            this.loading = true;
            if (this.isNetworkSteam) {
                const { data } = await this.$rq.getSteamTreatmentPrograms(this.network.id);
                this.treatments = data;
            } else if (this.network.id) {
                const queryParams = this.component.id ? { component: this.component.id } : {};
                const { data } = await this.$rq.getTreatmentPrograms(this.network.id, queryParams);
                this.treatments = data;
            }
            this.loading = false;
        },
        getUnits(key, index) {
            return this.treatmentProgramUnits[key]?.find(el => el.key === index)?.name;
        },
        getFunction(content) {
            return Object.keys(content).map(el => {
                if (content[el]) {
                    if (this.isNetworkSteam) return this.$t(`treatmentProgram.steam.step1.${el}`);

                    return this.$t(`treatmentProgram.step1.${el}`);
                }
                return '';
            })
                .filter(el => el.length)
                .join(', ');
        },
        getInjectionPoint(injectionPoint) {
            return Object.keys(injectionPoint).map(el => {
                if (injectionPoint[el]) {
                    return this.$t(`treatmentProgram.steam.step2.${el}`);
                }
                return '';
            })
                .filter(el => el.length)
                .join(', ');
        },
        getLimits(norms) {
            if (!norms) {
                return null;
            }

            const { minimum, maximum } = norms;
            const min = minimum || '';
            const max = maximum || '';

            return `${min} - ${max}`;
        },
        onClickAdd() {
            const componentName = this.isNetworkSteam ? steamForm : Main;
            this.$root.$emit('showModal', {
                title: this.$t('treatmentPrograms.add'),
                component: Vue.component('Main', componentName),
                payload: {
                    network: this.network,
                },
                showNextPrevious: true,
                onConfirm: requestPayload => this.addTreatmentProgram(requestPayload),
            });
        },
        async onClickAction(treatment, action) {
            if (action.mode === 'edit') {
                const componentName = this.isNetworkSteam ? steamForm : Main;
                const data = cloneDeep(this.treatments.find(tr => tr.id === treatment.id));
                if (!this.isNetworkSteam && data.programInfo?.siteProduct) {
                    data.programInfo.siteProduct = data.programInfo.siteProduct.id;
                }
                this.$root.$emit('showModal', {
                    title: this.$t('treatmentPrograms.update'),
                    showNextPrevious: true,
                    component: Vue.component('Main', componentName),
                    payload: { nodeInfo: cloneDeep(data), isEdition: true },
                    onConfirm: requestPayload => this.updateTreatmentProgram(treatment.id, requestPayload),
                });
            } else if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('treatmentPrograms.remove'),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: { msg: this.$t('treatmentPrograms.confirmRemove') },
                    onConfirm: () => (this.isNetworkSteam ? this.removeSteamTreatment(treatment) : this.removeTreatmentProgram(treatment)),
                    isForm: false,
                });
            }
        },
        async addTreatmentProgram(requestPayload) {
            const payload = this.component.id ? { ...requestPayload, component: this.component.id } : requestPayload;
            if (this.isNetworkSteam) await this.$rq.addSteamTreatmentProgram(this.network.id, payload);
            else await this.$rq.addTreatmentProgram(this.network.id, payload);


            this.$toasted.global.toast_success({
                message: this.$t('treatmentPrograms.added'),
            });
            await this.getTreatmentPrograms();
        },
        async updateTreatmentProgram(id, requestPayload) {
            if (requestPayload?.component?.id) {
                // eslint-disable-next-line no-param-reassign
                requestPayload.component = requestPayload.component.id;
            }
            if (requestPayload.history) {
                // eslint-disable-next-line no-param-reassign
                requestPayload.history = requestPayload.history.map(h => ({
                    ...h,
                    updatedBy: h.memberId,
                }));
            }
            // eslint-disable-next-line no-param-reassign
            if (typeof (requestPayload.siteProduct) === 'object') requestPayload.siteProduct = requestPayload.siteProduct.id;
            if (this.isNetworkSteam) await this.$rq.updateSteamTreatmentProgram(this.network.id, id, requestPayload);
            else await this.$rq.updateTreatmentProgram(this.network.id, id, requestPayload);

            this.$toasted.global.toast_success({
                message: this.$t('treatmentPrograms.updated'),
            });
            await this.getTreatmentPrograms();
        },
        async removeTreatmentProgram(treatment) {
            const response = await this.$rq.removeTreatmentProgram(this.network.id, treatment.id);
            if (response.status === Http.DELETE) {
                this.$toasted.global.toast_success({
                    message: this.$t('treatmentPrograms.removed'),
                });
                await this.getTreatmentPrograms();
            }
        },
        async removeSteamTreatment(treatment) {
            const response = await this.$rq.removeSteamTreatmentProgram(this.network.id, treatment.id);
            if (response.status === Http.DELETE) {
                this.$toasted.global.toast_success({
                    message: this.$t('treatmentPrograms.removed'),
                });
                await this.getTreatmentPrograms();
            }
        },
        displaySteamTreatment() {
            return this.treatments.map(treatment => ({
                id: treatment.id,
                productName: treatment?.siteProduct?.product?.name,
                function: this.getFunction(treatment.function),
                injectionPoint: this.getInjectionPoint(treatment.injectionPoint),
                targetDosage: treatment.targetDosage.ppm ? `${treatment.targetDosage.ppm} ppm` : '-',
                injectionControl: treatment.injectionControl.injection || '-',
            }));
        },
    },
};
</script>
<style lang="scss" scoped>
::v-deep .text-start{
    text-align: center !important;
}
</style>
